import React, { useState } from "react";
import {
    ArrowDownTrayIcon,
    ArrowTopRightOnSquareIcon,
    CheckIcon,
    ClipboardIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";

// /settings — the page that turns someone who just logged in into someone whose
// Claude is connected.
//
// ASCAA's brain runs inside the reader's own Claude, not inside this site, so
// setup is four things in a fixed order: be logged in here, have a Claude, give
// it the skill, point it at this server. The reader is a manager between cases,
// not a developer, so every value they must paste is its own copy button — the
// four in step 4 are the four fields of claude.ai's connector dialog, in the
// order that dialog asks for them.
const MCP_URL = "https://mcp.ascaa.lamoom.com/mcp";
const CONNECTOR_NAME = "ASCAA";
const CLIENT_ID = "4uhn5o89q706nt99d0hhck9ou6";
// McpConnectorConfidential is a confidential Cognito client, so the token
// exchange fails without this secret. It compiles into main.*.js, which
// CloudFront serves to anyone; alone it opens nothing, since a login and a code
// from claude.ai's callback are still required.
const CLIENT_SECRET = "u3qaorjn51fmv9m898apfgtlqito5eqek7jglrejilu1nusl2q7";
const ADD_COMMAND = `claude mcp add ascaa --transport http ${MCP_URL}`;

function CopyRow({ label, value, mono = true }) {
    const [copied, setCopied] = useState(false);
    return (
        <div>
            <div className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
            <div className="flex items-stretch gap-2">
                <code
                    className={`flex-1 min-w-0 overflow-x-auto whitespace-nowrap rounded-md bg-gray-900 px-4 py-3 text-sm text-gray-100 ${
                        mono ? "font-mono" : ""
                    }`}
                >
                    {value}
                </code>
                <button
                    type="button"
                    onClick={() => {
                        navigator.clipboard.writeText(value);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 1500);
                    }}
                    className="flex shrink-0 items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
                >
                    {copied ? <CheckIcon className="h-4 w-4 text-green-600" /> : <ClipboardIcon className="h-4 w-4" />}
                    {copied ? "Copied" : "Copy"}
                </button>
            </div>
        </div>
    );
}

function Step({ n, title, done, children }) {
    return (
        <section className="border-t border-gray-200 py-8">
            <div className="flex items-baseline gap-3">
                {done ? (
                    <CheckCircleIcon className="h-6 w-6 shrink-0 translate-y-1 text-green-600" />
                ) : (
                    <span className="flex h-6 w-6 shrink-0 translate-y-1 items-center justify-center rounded-full bg-gray-900 text-xs font-semibold text-white">
                        {n}
                    </span>
                )}
                <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            </div>
            <div className="mt-3 pl-9">{children}</div>
        </section>
    );
}

export default function Settings() {
    return (
        <div className="mx-auto max-w-3xl px-6 py-10">
            <h1 className="text-2xl font-bold text-gray-900">Set up Claude for ASCAA</h1>
            <p className="mb-8 mt-2 text-gray-600">
                Schedule from Claude instead of by hand. Four steps, about five minutes. At the
                end you can ask for a month and get back a draft you approve here.
            </p>

            <Step n={1} title="Sign in to ASCAA" done>
                <p className="text-sm text-gray-600">
                    Done &mdash; you are signed in, or you would not be reading this. Colleagues
                    sign in with their ASCAA email and password at{" "}
                    <a
                        href="https://ascaa.lamoom.com/users"
                        className="text-blue-600 hover:underline"
                    >
                        ascaa.lamoom.com/users
                    </a>
                    . A manager adds them there first.
                </p>
            </Step>

            <Step n={2} title="Get Claude">
                <p className="text-sm text-gray-600">
                    Register at{" "}
                    <a
                        href="https://claude.ai"
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                    >
                        claude.ai
                        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                    </a>{" "}
                    if you do not have an account, or install the Claude desktop app. Either one
                    works. Use the browser if you are not sure.
                </p>
            </Step>

            <Step n={3} title="Add the ASCAA skill">
                <p className="text-sm text-gray-600">
                    The skill is what makes Claude reach for ASCAA. It tells Claude to read
                    this group&rsquo;s scheduling instructions from the connector in step 4, so
                    those rules stay in one place. Download it, then in Claude go to{" "}
                    <span className="font-medium text-gray-900">
                        Settings &rarr; Capabilities &rarr; Skills
                    </span>{" "}
                    and upload the file.
                </p>
                <a
                    href="/downloads/ascaa.skill"
                    download
                    className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    Download ascaa.skill
                </a>
            </Step>

            <Step n={4} title="Add the ASCAA connector">
                <p className="text-sm text-gray-600">
                    This is what lets Claude read your hospitals and write drafts. In Claude,
                    open the <span className="font-medium text-gray-900">+</span> menu beside the
                    message box, then{" "}
                    <span className="font-medium text-gray-900">
                        Connectors &rarr; Add connector &rarr; Add custom connector
                    </span>
                    . Fill the dialog with these four values &mdash; the last two are behind{" "}
                    <span className="font-medium text-gray-900">Advanced settings</span>.
                </p>
                <figure className="mt-4">
                    <img
                        src="/setup/connector-menu.png"
                        alt="The plus menu in Claude, opened to Connectors, then Add connector, then Add custom connector."
                        className="w-full rounded-lg border border-gray-200"
                    />
                    <figcaption className="mt-2 text-xs text-gray-500">
                        Where the menu lives. Your list of connectors will look different from
                        this one.
                    </figcaption>
                </figure>
                <figure className="mt-6">
                    <img
                        src="/setup/connector-dialog.png"
                        alt="The Add custom connector dialog, ASCAA typed in the name field, Advanced settings expanded to show OAuth Client ID and OAuth Client Secret."
                        className="w-full rounded-lg border border-gray-200"
                    />
                    <figcaption className="mt-2 text-xs text-gray-500">
                        The dialog, with <span className="font-medium text-gray-700">Advanced settings</span>{" "}
                        opened. Type <span className="font-medium text-gray-700">ASCAA</span> in the
                        first field &mdash; the name shown here is from another connector.
                    </figcaption>
                </figure>
                <div className="mt-6 space-y-4">
                    <CopyRow label="Name" value={CONNECTOR_NAME} mono={false} />
                    <CopyRow label="Remote MCP server URL" value={MCP_URL} />
                    <CopyRow label="OAuth Client ID" value={CLIENT_ID} />
                    <CopyRow label="OAuth Client Secret" value={CLIENT_SECRET} />
                </div>
                <p className="mt-4 text-sm text-gray-600">
                    Press <span className="font-medium text-gray-900">Add</span>, then sign in
                    with the same ASCAA account you use here. Claude sees exactly what your role
                    sees on this site.
                </p>
                <p className="mt-2 text-xs text-gray-500">
                    These two values belong to this group. Treat them like a key to the building:
                    they are safe to use, not to repost.
                </p>
            </Step>

            <Step n={5} title="Ask for a month">
                <p className="mb-3 text-sm text-gray-600">
                    Say the skill&rsquo;s name &mdash; <span className="font-medium text-gray-900">ascaa</span>{" "}
                    &mdash; so Claude schedules the way this group does instead of guessing. It then
                    reads the hospital&rsquo;s preference, every provider&rsquo;s cap and everything
                    already booked, writes a draft, and sends back a PDF.
                </p>
                <CopyRow
                    label="Try this"
                    value="Use the ascaa skill: schedule Hazel Hawkins for September"
                    mono={false}
                />
                <p className="mt-3 text-sm text-gray-600">
                    Nothing it writes is final. Drafts land on the{" "}
                    <a href="/calendar" className="text-blue-600 hover:underline">
                        Calendar
                    </a>{" "}
                    for a manager to approve, and every run is recorded under{" "}
                    <a href="/agent-files" className="text-blue-600 hover:underline">
                        Agent Files
                    </a>
                    .
                </p>
            </Step>

            <section className="border-t border-gray-200 pt-8">
                <h2 className="text-sm font-semibold text-gray-900">Using Claude Code instead?</h2>
                <p className="mb-3 mt-1 text-sm text-gray-600">
                    One line in your terminal, then <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-xs">/mcp</code>{" "}
                    once to sign in. It discovers its own login, so it needs no client id or
                    secret.
                </p>
                <CopyRow label="Terminal" value={ADD_COMMAND} />
                <p className="mt-3 text-sm text-gray-500">
                    Prefer a file? The same skill and server, packaged per client:{" "}
                    <a href="/downloads/ascaa-claude-plugin.zip" download className="text-blue-600 hover:underline">
                        Claude Code plugin
                    </a>{" "}
                    &middot;{" "}
                    <a href="/downloads/ascaa-plugin.zip" download className="text-blue-600 hover:underline">
                        any other MCP client
                    </a>
                    .
                </p>
            </section>
        </div>
    );
}
